import { useState, useCallback, lazy, Suspense } from 'react';
import { PanelLeftClose, PanelLeft, Layers, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import { useQueryClient } from '@tanstack/react-query';
import TemplateStudioLayout from '../components/layout/TemplateStudioLayout';
import TemplateTree from '../components/studio/TemplateTree';
import TemplateDetail from '../components/studio/TemplateDetail';
import ForkDialog from '../components/studio/ForkDialog';
import NewTemplateDialog from '../components/studio/NewTemplateDialog';
import RulesList from '../components/studio/rules/RulesList';
import RuleBuilder from '../components/studio/rules/RuleBuilder';
import DiffViewer from '../components/studio/DiffViewer';
import { Button } from '../components/ui/button';
import {
  useTemplates,
  useTemplate,
  useCreateTemplate,
  useUpdateContent,
  useActivateVersion,
  useForkTemplate,
  useTemplateAudit,
} from '../hooks/useTemplates';
import { useRulesForTemplate, useCreateRule, useUpdateRule } from '../hooks/useRules';
import { templateStudioService } from '../services/template-studio.service';
import { useSelectedTenant } from '../../../stores/tenantStore';
import type { TemplateTreeNode, TemplateVersion, TemplateDetail as TemplateDetailType, TemplateScope } from '../types/template.types';
import type { Rule, ConditionGroup } from '../types/rule.types';

// Lazy load Monaco editor component
const TemplateEditor = lazy(() => import('../components/studio/TemplateEditor'));

type View = 'detail' | 'editor' | 'diff' | 'rule-builder';

export default function TemplateStudioPage() {
  const { selectedTenant } = useSelectedTenant();
  const tenantId = selectedTenant?.id ?? '';

  const [selectedNode, setSelectedNode] = useState<TemplateTreeNode | null>(null);
  const [view, setView] = useState<View>('detail');
  const [forkOpen, setForkOpen] = useState(false);
  const [newTemplateOpen, setNewTemplateOpen] = useState(false);
  const [newTemplateDefaultScope, setNewTemplateDefaultScope] = useState<TemplateScope>('tenant');
  const [diffState, setDiffState] = useState<{ oldVersion: TemplateVersion; newVersion: TemplateVersion } | null>(null);
  const [editingRule, setEditingRule] = useState<Rule | null>(null);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [detailTab, setDetailTab] = useState('overview');

  const queryClient = useQueryClient();

  // Data hooks
  const { data: tree, isLoading: treeLoading } = useTemplates(tenantId || undefined);
  const { data: templateDetail } = useTemplate(
    tenantId || undefined,
    selectedNode?.id || undefined
  );
  const { data: rules = [], isLoading: rulesLoading } = useRulesForTemplate(
    tenantId || undefined,
    selectedNode?.id || undefined
  );
  const { data: auditLog } = useTemplateAudit(
    tenantId || undefined,
    selectedNode?.id || undefined
  );

  // Mutations
  const createTemplate = useCreateTemplate(tenantId);
  const updateContent = useUpdateContent(tenantId, selectedNode?.id ?? '');
  const activateVersion = useActivateVersion(tenantId, selectedNode?.id ?? '');
  const forkTemplate = useForkTemplate(tenantId);
  const createRule = useCreateRule(tenantId);
  const updateRule = useUpdateRule(tenantId, selectedNode?.id ?? '');

  const handleSelect = (t: TemplateTreeNode) => {
    setSelectedNode(t);
    setView('detail');
    setDetailTab('overview');
  };

  const handleDiff = (v1: TemplateVersion, v2: TemplateVersion) => {
    setDiffState({ oldVersion: v1, newVersion: v2 });
    setView('diff');
  };

  const handleActivate = async (version: TemplateVersion) => {
    try {
      await activateVersion.mutateAsync(version.version);
      toast.success(`Activated v${version.version}`);
    } catch {
      toast.error('Failed to activate version');
    }
  };

  const handleEditRule = (rule: Rule) => {
    setEditingRule(rule);
    setView('rule-builder');
  };

  const handleAddRule = () => {
    if (!selectedNode) return;
    const newRule: Rule = {
      ruleId: `new-${Date.now()}`,
      tenantId,
      templateId: selectedNode.id,
      name: 'New Rule',
      description: '',
      status: 'draft',
      eventTypes: [],
      priority: 99,
      stopAfterMatch: false,
      conditionGroup: {
        id: `cg-${Date.now()}`,
        logic: 'AND',
        conditions: [],
        groups: [],
      },
      version: 1,
      createdBy: '',
      updatedBy: '',
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };
    setEditingRule(newRule);
    setView('rule-builder');
  };

  const handleSaveRule = async (updated: Rule) => {
    try {
      if (updated.ruleId.startsWith('new-')) {
        await createRule.mutateAsync({
          templateId: updated.templateId,
          name: updated.name,
          description: updated.description,
          status: updated.status,
          eventTypes: updated.eventTypes,
          priority: updated.priority,
          stopAfterMatch: updated.stopAfterMatch,
          conditionGroup: updated.conditionGroup,
        });
        toast.success(`Rule "${updated.name}" created`);
      } else {
        await updateRule.mutateAsync({
          ruleId: updated.ruleId,
          name: updated.name,
          description: updated.description,
          status: updated.status,
          eventTypes: updated.eventTypes,
          priority: updated.priority,
          stopAfterMatch: updated.stopAfterMatch,
          conditionGroup: updated.conditionGroup,
        });
        toast.success(`Rule "${updated.name}" saved`);
      }
      setView('detail');
      setEditingRule(null);
      setDetailTab('rules');
    } catch {
      toast.error('Failed to save rule');
    }
  };

  const handleSaveContent = async (content: string, commitMessage: string) => {
    try {
      await updateContent.mutateAsync({ content, commitMessage });
      toast.success('Template saved');
      setView('detail');
    } catch {
      toast.error('Failed to save template');
    }
  };

  const handleCreateTemplate = async (data: Parameters<typeof createTemplate.mutateAsync>[0] & { scope: TemplateScope }) => {
    try {
      const { scope, ...templateData } = data;
      if (scope === 'base') {
        await templateStudioService.createTemplate('base', templateData);
        queryClient.invalidateQueries({ queryKey: ['template-studio', 'templates'] });
      } else {
        await createTemplate.mutateAsync(templateData);
      }
      toast.success(`Template "${data.name}" created`);
      setNewTemplateOpen(false);
    } catch {
      toast.error('Failed to create template');
    }
  };

  const handleFork = async (sourceTemplateId: string, name?: string) => {
    try {
      await forkTemplate.mutateAsync({ sourceTemplateId, name });
      toast.success('Template forked successfully');
      setForkOpen(false);
    } catch {
      toast.error('Failed to fork template');
    }
  };

  const handleSync = () => {
    queryClient.invalidateQueries({ queryKey: ['template-studio'] });
    toast.info('Syncing with GitHub...');
  };

  if (!tenantId) {
    return (
      <TemplateStudioLayout>
        <div className="flex h-[calc(100vh-4rem)] items-center justify-center">
          <div className="text-center space-y-2">
            <Layers className="h-10 w-10 text-muted-foreground/30 mx-auto" />
            <p className="text-sm text-muted-foreground">Select a tenant to open Template Studio</p>
          </div>
        </div>
      </TemplateStudioLayout>
    );
  }

  return (
    <TemplateStudioLayout>
      <div className="flex h-[calc(100vh-4rem)] flex-col bg-studio-shell">
        {/* Top Nav */}
        <header className="flex h-11 items-center justify-between border-b border-border bg-studio-nav px-4">
          <div className="flex items-center gap-2.5">
            <Layers className="h-4 w-4 text-studio-nav-foreground" />
            <span className="text-sm font-semibold text-studio-nav-foreground">Template Studio</span>
            {selectedNode && view !== 'detail' && (
              <>
                <span className="text-studio-nav-foreground/30 text-xs">/</span>
                <span className="text-xs text-studio-nav-foreground/70 font-mono">{selectedNode.path}</span>
              </>
            )}
          </div>
          <div className="flex items-center gap-1">
            <Button
              variant="ghost"
              size="sm"
              className="h-7 w-7 p-0 text-studio-nav-foreground/70 hover:text-studio-nav-foreground hover:bg-studio-nav-foreground/10"
              onClick={() => setSidebarOpen(!sidebarOpen)}
            >
              {sidebarOpen ? <PanelLeftClose className="h-3.5 w-3.5" /> : <PanelLeft className="h-3.5 w-3.5" />}
            </Button>
            <Button
              variant="ghost"
              size="sm"
              className="h-7 text-xs gap-1.5 text-studio-nav-foreground/70 hover:text-studio-nav-foreground hover:bg-studio-nav-foreground/10"
              onClick={handleSync}
              data-tour="sync-btn"
            >
              <RefreshCw className="h-3 w-3" />
              Sync
            </Button>
          </div>
        </header>

        {/* Main */}
        <div className="flex flex-1 min-h-0">
          {/* Sidebar */}
          <div className={`flex-shrink-0 transition-all duration-200 ${sidebarOpen ? 'w-[280px]' : 'w-0 overflow-hidden'}`}>
            <TemplateTree
              selectedId={selectedNode?.id || null}
              baseTemplates={tree?.base ?? []}
              tenantTemplates={tree?.tenant ?? []}
              onSelect={handleSelect}
              onNewTemplate={() => { setNewTemplateDefaultScope('tenant'); setNewTemplateOpen(true); }}
              onNewBaseTemplate={() => { setNewTemplateDefaultScope('base'); setNewTemplateOpen(true); }}
              onForkTemplate={() => setForkOpen(true)}
              isLoading={treeLoading}
            />
          </div>

          {/* Stage */}
          <div className="flex-1 min-w-0">
            {!selectedNode ? (
              <div className="flex h-full items-center justify-center">
                <div className="text-center space-y-2">
                  <Layers className="h-10 w-10 text-muted-foreground/30 mx-auto" />
                  <p className="text-sm text-muted-foreground">Select a template to get started</p>
                </div>
              </div>
            ) : view === 'editor' && templateDetail ? (
              <Suspense fallback={<div className="flex h-full items-center justify-center text-xs text-muted-foreground">Loading editor...</div>}>
                <TemplateEditor
                  template={templateDetail}
                  onBack={() => setView('detail')}
                  onSave={handleSaveContent}
                  isSaving={updateContent.isPending}
                />
              </Suspense>
            ) : view === 'diff' && diffState ? (
              <DiffViewer
                oldContent=""
                newContent=""
                oldLabel={`v${diffState.oldVersion.version} · ${diffState.oldVersion.commitSha.slice(0, 7)}`}
                newLabel={`v${diffState.newVersion.version} · ${diffState.newVersion.commitSha.slice(0, 7)}`}
                templatePath={selectedNode.path}
                onBack={() => setView('detail')}
              />
            ) : view === 'rule-builder' && editingRule ? (
              <RuleBuilder
                rule={editingRule}
                onBack={() => { setView('detail'); setEditingRule(null); }}
                onSave={handleSaveRule}
                isSaving={createRule.isPending || updateRule.isPending}
              />
            ) : templateDetail ? (
              <TemplateDetail
                template={templateDetail}
                rules={rules}
                onEdit={() => setView('editor')}
                onDiff={handleDiff}
                onActivate={handleActivate}
                onEditRule={handleEditRule}
                onAddRule={handleAddRule}
                onEditInfo={() => {}}
                activeTab={detailTab}
                onTabChange={setDetailTab}
                auditLog={auditLog}
                renderRulesTab={
                  <RulesList
                    rules={rules}
                    onEditRule={handleEditRule}
                    onAddRule={handleAddRule}
                    isLoading={rulesLoading}
                  />
                }
                renderRuleTestTab={
                  <div className="text-center py-8 text-xs text-muted-foreground">
                    Rule simulation coming soon
                  </div>
                }
                renderTestRenderTab={
                  <div className="text-center py-8 text-xs text-muted-foreground">
                    Test rendering — use the editor for live preview
                  </div>
                }
              />
            ) : (
              <div className="flex h-full items-center justify-center text-xs text-muted-foreground">
                Loading template...
              </div>
            )}
          </div>
        </div>

        <ForkDialog
          open={forkOpen}
          onClose={() => setForkOpen(false)}
          baseTemplates={tree?.base ?? []}
          onFork={handleFork}
          isForking={forkTemplate.isPending}
        />
        <NewTemplateDialog
          open={newTemplateOpen}
          onClose={() => setNewTemplateOpen(false)}
          onCreate={handleCreateTemplate}
          isCreating={createTemplate.isPending}
          defaultScope={newTemplateDefaultScope}
        />
      </div>
    </TemplateStudioLayout>
  );
}
